import React from "react";

interface SVGDefsProps {
  mode: string;
  brand: {
    deep: string;
    aqua: string;
    violet: string;
    amber: string;
    white: string;
    black: string;
    green: string;
  };
}

export function SVGDefs({ mode, brand }: SVGDefsProps) {
  return (
    <defs>
      {/* Lens gradients for convex/concave */}
      <radialGradient id="lensConvex" cx="50%" cy="45%" r="46%">
        <stop offset="0%" stopColor={brand.white} stopOpacity="0.12" />
        <stop offset="60%" stopColor={brand.violet} stopOpacity="0.06" />
        <stop offset="100%" stopColor={brand.deep} stopOpacity="0.0" />
      </radialGradient>
      <radialGradient id="lensConcave" cx="50%" cy="55%" r="50%">
        <stop offset="0%" stopColor={brand.deep} stopOpacity="0.0" />
        <stop offset="65%" stopColor={brand.violet} stopOpacity="0.10" />
        <stop offset="100%" stopColor={brand.white} stopOpacity="0.12" />
      </radialGradient>

      {/* Hex cell (pointy-top) */}
      <symbol id="hex" viewBox="-1 -1 2 2">
        <polygon points="0,-1 0.866,-0.5 0.866,0.5 0,1 -0.866,0.5 -0.866,-0.5" />
      </symbol>
      {["aqua", "violet", "amber"].map((tone) => (
        <pattern key={tone} id={`hex-${tone}`} width="20" height="17.32" patternUnits="userSpaceOnUse">
          <use href="#hex" x="10" y="8.66" width="20" height="17.32" fill="none" stroke={brand[tone as keyof typeof brand]} strokeWidth="0.7" />
          <use href="#hex" x="0" y="0" width="20" height="17.32" fill="none" stroke={brand[tone as keyof typeof brand]} strokeWidth="0.7" />
          <use href="#hex" x="20" y="0" width="20" height="17.32" fill="none" stroke={brand[tone as keyof typeof brand]} strokeWidth="0.7" />
        </pattern>
      ))}

      {/* Checker pattern */}
      <pattern id="checker" width="24" height="24" patternUnits="userSpaceOnUse">
        <rect x="0" y="0" width="24" height="24" fill="none" />
        <rect x="0" y="0" width="12" height="12" fill={brand.white} opacity="0.08" />
        <rect x="12" y="12" width="12" height="12" fill={brand.white} opacity="0.08" />
      </pattern>

      {/* Spiral fade */}
      <radialGradient id="spiralFade" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={brand.white} stopOpacity="0.12" />
        <stop offset="100%" stopColor={brand.deep} stopOpacity="0" />
      </radialGradient>

      {/* Diamond grid */}
      <pattern id="diamond" width="28" height="28" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect x="0" y="0" width="28" height="28" fill="none" />
        <path
          d="M0 14 L28 14 M14 0 L14 28"
          stroke={mode === "grandma" ? brand.black : brand.white}
          strokeOpacity={mode === "grandma" ? 0.12 : 0.18}
          strokeWidth="1"
        />
      </pattern>

      {/* Isometric cubes */}
      <pattern id="iso" width="36" height="31.18" patternUnits="userSpaceOnUse">
        <g stroke={mode === "grandma" ? brand.black : brand.white} strokeOpacity={mode === "grandma" ? 0.22 : 0.18} strokeWidth="0.8" fill="none">
          <polygon points="18,0 36,10.39 36,20.79 18,31.18 0,20.79 0,10.39" />
          <line x1="18" y1="0" x2="18" y2="31.18" />
          <line x1="0" y1="10.39" x2="36" y2="10.39" />
          <line x1="0" y1="20.79" x2="36" y2="20.79" />
        </g>
      </pattern>

      {/* Shield outline */}
      <symbol id="shield" viewBox="-160 -180 320 360">
        <path d="M0,-160 C90,-160 150,-100 150,-30 V40 C150,100 70,150 0,170 C-70,150 -150,100 -150,40 V-30 C-150,-100 -90,-160 0,-160 Z" />
      </symbol>
      <pattern id="chevrons" width="18" height="18" patternUnits="userSpaceOnUse" patternTransform="skewX(10)">
        <path
          d="M0 9 L9 0 L18 9 L9 18 Z"
          fill="none"
          stroke={mode === "grandma" ? brand.black : brand.white}
          strokeOpacity={0.22}
          strokeWidth="1.3"
        />
      </pattern>

      {/* Padlock symbol */}
      <symbol id="padlock" viewBox="-120 -140 240 280">
        <rect x="-80" y="-40" width="160" height="150" rx="18" ry="18" />
        <path d="M-44,-40 v-30 a64,64 0 0 1 128,0 v30 h-24 v-30 a40,40 0 0 0 -80,0 v30 z" />
        <circle cx="0" cy="30" r="14" />
        <rect x="-6" y="30" width="12" height="34" rx="6" />
      </symbol>

      {/* Brick pattern for firewall */}
      <pattern id="brick" width="40" height="20" patternUnits="userSpaceOnUse">
        <rect x="0" y="0" width="40" height="20" fill="none" />
        <rect x="0" y="0" width="20" height="10" fill={mode === "grandma" ? brand.black : brand.white} opacity={0.1} />
        <rect x="20" y="0" width="20" height="10" fill={mode === "grandma" ? brand.black : brand.white} opacity={0.1} />
        <rect x="-10" y="10" width="20" height="10" fill={mode === "grandma" ? brand.black : brand.white} opacity={0.1} />
        <rect x="10" y="10" width="20" height="10" fill={mode === "grandma" ? brand.black : brand.white} opacity={0.1} />
        <rect x="30" y="10" width="20" height="10" fill={mode === "grandma" ? brand.black : brand.white} opacity={0.1} />
      </pattern>

      {/* Circuit trace pattern */}
      <pattern id="traces" width="60" height="60" patternUnits="userSpaceOnUse">
        <rect width="60" height="60" fill="none" />
        <path d="M6 12 H52 V20 H20 V32 H44 V40 H12" stroke={mode === "grandma" ? brand.black : brand.aqua} strokeWidth="2" fill="none" />
        <circle cx="6" cy="12" r="2.5" fill={mode === "grandma" ? brand.black : brand.amber} />
        <circle cx="52" cy="20" r="2.5" fill={mode === "grandma" ? brand.black : brand.violet} />
        <circle cx="20" cy="32" r="2.5" fill={mode === "grandma" ? brand.black : brand.amber} />
        <circle cx="44" cy="40" r="2.5" fill={mode === "grandma" ? brand.black : brand.violet} />
      </pattern>

      {/* Fingerprint rings */}
      <radialGradient id="fpDepth" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={mode === "grandma" ? brand.black : brand.white} stopOpacity="0.08" />
        <stop offset="100%" stopColor={mode === "grandma" ? brand.white : brand.deep} stopOpacity="0" />
      </radialGradient>

      {/* ===== Agam × Vasarely helpers ===== */}
      <linearGradient id="spectrum" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#FF2D55" />
        <stop offset="14%" stopColor="#FF3B30" />
        <stop offset="28%" stopColor="#FF9500" />
        <stop offset="42%" stopColor="#FFD60A" />
        <stop offset="56%" stopColor="#34C759" />
        <stop offset="70%" stopColor="#00FFFF" />
        <stop offset="84%" stopColor="#0A84FF" />
        <stop offset="100%" stopColor="#6C63FF" />
      </linearGradient>
      <radialGradient id="spectrumRadial" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FF2D55" />
        <stop offset="33%" stopColor="#FFD60A" />
        <stop offset="66%" stopColor="#00FFFF" />
        <stop offset="100%" stopColor="#6C63FF" />
      </radialGradient>
      <mask id="stripeEven">
        <rect x="0" y="0" width="794" height="1123" fill="black" />
        {Array.from({ length: 80 }).map((_, i) => (
          <rect key={i} x={i * 10} y="0" width="6" height="1123" fill="white" />
        ))}
      </mask>
      <mask id="stripeOdd">
        <rect x="0" y="0" width="794" height="1123" fill="black" />
        {Array.from({ length: 80 }).map((_, i) => (
          <rect key={i} x={i * 10 + 6} y="0" width="6" height="1123" fill="white" />
        ))}
      </mask>
      <clipPath id="circleClip">
        <circle cx="397" cy="540" r="310" />
      </clipPath>

      {/* ===== Calder × Howe helpers ===== */}
      <radialGradient id="howeHalo" cx="50%" cy="50%" r="48%">
        <stop offset="0%" stopColor={brand.white} stopOpacity="0.10" />
        <stop offset="65%" stopColor={brand.aqua} stopOpacity="0.08" />
        <stop offset="100%" stopColor={brand.deep} stopOpacity="0" />
      </radialGradient>
      <linearGradient id="bladeGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={brand.aqua} />
        <stop offset="100%" stopColor={brand.violet} />
      </linearGradient>
      <symbol id="blade" viewBox="-28 -28 56 56">
        <path d="M0,-24 C14,-18 22,-8 24,0 C22,8 14,18 0,24 C-12,18 -20,8 -22,0 C-20,-8 -12,-18 0,-24 Z" />
      </symbol>

      {/* ===== Tinguely × Jansen helpers ===== */}
      <symbol id="gear" viewBox="-40 -40 80 80">
        <circle r="18" fill="none" stroke={brand.white} strokeWidth="6" />
        {Array.from({ length: 12 }).map((_, i) => (
          <rect key={i} x={-3} y={-36} width={6} height={10} fill={brand.white} transform={`rotate(${i * 30})`} />
        ))}
        <circle r="6" fill={brand.deep} stroke={brand.aqua} strokeWidth="3" />
      </symbol>
      
      <symbol id="gearLarge" viewBox="-60 -60 120 120">
        <circle r="28" fill="none" stroke={brand.white} strokeWidth="8" />
        {Array.from({ length: 16 }).map((_, i) => (
          <rect key={i} x={-4} y={-52} width={8} height={14} fill={brand.white} transform={`rotate(${i * 22.5})`} />
        ))}
        <circle r="10" fill={brand.deep} stroke={brand.violet} strokeWidth="4" />
      </symbol>
      
      <symbol id="wheel" viewBox="-50 -50 100 100">
        <circle r="40" fill="none" stroke={brand.white} strokeWidth="3" />
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={i} x1="0" y1="0" x2={40 * Math.cos((i * Math.PI) / 4)} y2={40 * Math.sin((i * Math.PI) / 4)} stroke={brand.white} strokeWidth="2" />
        ))}
        <circle r="8" fill={brand.aqua} />
      </symbol>
      
      <symbol id="jansenLeg" viewBox="-60 -80 120 160">
        <line x1="0" y1="-60" x2="40" y2="-20" stroke={brand.white} strokeWidth="3" />
        <line x1="40" y1="-20" x2="50" y2="30" stroke={brand.white} strokeWidth="3" />
        <line x1="50" y1="30" x2="20" y2="70" stroke={brand.white} strokeWidth="3" />
        <line x1="20" y1="70" x2="-20" y2="60" stroke={brand.white} strokeWidth="3" />
        <line x1="-20" y1="60" x2="-40" y2="20" stroke={brand.white} strokeWidth="3" />
        <line x1="-40" y1="20" x2="0" y2="-60" stroke={brand.white} strokeWidth="3" />
        <circle cx="0" cy="-60" r="4" fill={brand.amber} />
        <circle cx="40" cy="-20" r="4" fill={brand.aqua} />
        <circle cx="50" cy="30" r="4" fill={brand.violet} />
        <circle cx="20" cy="70" r="5" fill={brand.green} />
      </symbol>
      
      <linearGradient id="sail" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={brand.aqua} />
        <stop offset="100%" stopColor={brand.violet} />
      </linearGradient>
      
      <linearGradient id="rust" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8B4513" />
        <stop offset="50%" stopColor="#D2691E" />
        <stop offset="100%" stopColor="#CD853F" />
      </linearGradient>
      
      <pattern id="metal" width="20" height="20" patternUnits="userSpaceOnUse">
        <rect x="0" y="0" width="20" height="20" fill={brand.deep} />
        <circle cx="5" cy="5" r="1" fill={brand.white} opacity="0.3" />
        <circle cx="15" cy="15" r="1" fill={brand.white} opacity="0.3" />
      </pattern>
    </defs>
  );
}
